<template>
  <div class="connector-detail-container">
    <div class="page-header">
      <div class="back-button" @click="goBack">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
        </svg>
      </div>
      <h1 class="text-2xl font-medium text-gray-800">Connect {{ sourceName }}</h1>
    </div>

    <div class="content-grid">
      <div class="left-column">
        <div class="source-name-section">
          <h2 class="section-title">Source Name</h2>
          <input type="text" :placeholder="'My ' + sourceName" class="source-name-input" />
          <p class="text-sm text-gray-500 mt-2">A name to identify this source in your workspace</p>
        </div>

        <div class="authentication-section mt-8">
          <h2 class="section-title">Authentication mechanism</h2>
          <p class="text-sm text-gray-500">Choose how to authenticate to {{ sourceName }}</p>
          
          <button class="oauth-button">
            <span class="oauth-icon">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M18 8a6 6 0 01-7.743 5.743L10 14l-1 1-1 1H6v-1l1-1 1-1 .257-.257A6 6 0 1118 8zm-6-4a1 1 0 100 2h2a1 1 0 100-2h-2z" clip-rule="evenodd" />
              </svg>
            </span>
            Connect with OAuth
          </button>
        </div>

        <div class="action-buttons mt-8">
          <button class="cancel-button" @click="goBack">Cancel</button>
          <button class="connect-button">Connect Source</button>
        </div>
      </div>

      <div class="right-column">
        <div class="setup-guide">
          <div class="setup-guide-header">
            <div class="source-icon">
              <img :src="sourceIcon" :alt="sourceName" class="h-6 w-6" />
            </div>
            <h2 class="text-lg font-medium">{{ sourceName }} Setup Guide</h2>
          </div>

          <div class="setup-steps">
            <h3 class="text-md font-medium text-gray-700 mb-4">Quick Setup</h3>
            
            <div class="step">
              <div class="step-number">1. Connect with OAuth</div>
              <div class="step-details">
                <div class="step-item">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  <span>Click "<strong>Connect with {{ sourceName }}</strong>"</span>
                </div>
                <div class="step-item">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  <span>Sign in to your {{ sourceName }} account</span>
                </div>
                <div class="step-item">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  <span>Grant access to your workspaces</span>
                </div>
              </div>
            </div>

            <div class="step">
              <div class="step-number">2. Configure Workspaces</div>
              <div class="step-details">
                <div class="step-item">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  <span>Select workspace IDs to sync from</span>
                </div>
                <div class="step-item">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  <span>Or leave empty to sync all accessible workspaces</span>
                </div>
              </div>
            </div>

            <div class="step">
              <div class="step-number">3. What gets synced?</div>
              <div class="step-details">
                <div class="step-item">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  <span>Projects, tasks, and team data</span>
                </div>
                <div class="step-item">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  <span>User information and assignments</span>
                </div>
                <div class="step-item">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  <span>Custom fields and portfolios</span>
                </div>
                <div class="step-item">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  <span>Last 30 days of data by default</span>
                </div>
              </div>
            </div>

            <div class="workspace-ids mt-6">
              <h3 class="text-md font-medium text-gray-700 mb-2">Workspace IDs</h3>
              <p class="text-sm text-gray-600">Find your workspace IDs in {{ sourceName }}'s URL or via their API. Format:</p>
              <div class="id-example">
                123456789123456
              </div>
            </div>

            <div class="tips mt-6">
              <h3 class="text-md font-medium text-gray-700 mb-2">Tips</h3>
              <div class="step-details">
                <div class="step-item">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  <span>Make sure you have access to the workspaces you want to sync</span>
                </div>
                <div class="step-item">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  <span>Large workspaces may take time to sync initially</span>
                </div>
                <div class="step-item">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  <span>Data includes task history and comments</span>
                </div>
              </div>
            </div>

            <div class="advanced-config mt-6">
              <button class="advanced-config-button">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
                Advanced Configuration Details
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ConnectorDetailPage',
  props: {
    sourceId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      sources: {
        'agdata': {
          name: 'AgData',
          icon: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSFe6CIeHyiBKUw_8dWpCTjTh4NlhzC0bz58A&s'
        },
        'agrichain': {
          name: 'AgriChain',
          icon: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRbmXpDhr1e3Us8J6cqTwaBn-98Z3Vct70Pww&s'
        },
        'agridigital': {
          name: 'AgriDigital',
          icon: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR65M2J5KfHBCqpYbdb0cBmXiICYndrJrpsaQ&s'
        },
        'agriwebb': {
          name: 'AgriWebb',
          icon: 'https://avatars.githubusercontent.com/u/13633868?s=200&v=4'
        },
        'agronomeye': {
          name: 'Agronomeye',
          icon: 'https://i.tracxn.com/logo/company/agronomeye.com.au_Logo_01455d31-c57d-403c-99e7-57abb1c17e6d.jpg?format=webp&height=120&width=120'
        },
        'agworld': {
          name: 'Agworld',
          icon: 'https://play-lh.googleusercontent.com/TCMY0m7BESynZnE_XqRtH2kDUwjazlGegLksM-g8ebuDZjN8G3G8bEn7RCKVLiGtQCA'
        },
        'file': {
          name: 'File',
          icon: 'https://connectors.airbyte.com/files/metadata/airbyte/source-file/latest/icon.svg'
        },
        'gmail': {
          name: 'Gmail',
          icon: 'https://dev.junify.projuno.com/images/icons/sources/gmail.svg'
        },
        'google-calendar': {
          name: 'Google Calendar',
          icon: 'https://dev.junify.projuno.com/images/icons/sources/google-calendar.svg'
        },
        'google-drive': {
          name: 'Google Drive',
          icon: 'https://connectors.airbyte.com/files/metadata/airbyte/source-google-drive/latest/icon.svg'
        },
        'google-sheets': {
          name: 'Google Sheets',
          icon: 'https://connectors.airbyte.com/files/metadata/airbyte/source-google-sheets/latest/icon.svg'
        },
        'microsoft-excel': {
          name: 'Microsoft Excel',
          icon: 'https://dev.junify.projuno.com/images/icons/sources/microsoft-excel.svg'
        },
        'microsoft-onedrive': {
          name: 'Microsoft OneDrive',
          icon: 'https://connectors.airbyte.com/files/metadata/airbyte/source-microsoft-onedrive/latest/icon.svg'
        },
        'microsoft-teams': {
          name: 'Microsoft Teams',
          icon: 'https://connectors.airbyte.com/files/metadata/airbyte/source-microsoft-teams/latest/icon.svg'
        },
        'mysql': {
          name: 'MySQL',
          icon: 'https://connectors.airbyte.com/files/metadata/airbyte/source-mysql/latest/icon.svg'
        },
        'outlook': {
          name: 'Outlook',
          icon: 'https://connectors.airbyte.com/files/metadata/airbyte/source-outlook/latest/icon.svg'
        },
        'quickbooks': {
          name: 'Quickbooks',
          icon: 'https://connectors.airbyte.com/files/metadata/airbyte/source-quickbooks/latest/icon.svg'
        },
        'xero': {
          name: 'Xero',
          icon: 'https://connectors.airbyte.com/files/metadata/airbyte/source-xero/latest/icon.svg'
        },
        'bureau-of-meteorology': {
          name: 'Bureau of Meteorology',
          icon: 'https://www.bom.gov.au/sites/default/files/styles/778/public/2020-11/BOM-Weather-app-icon-square.png.webp'
        }
      }
    }
  },
  computed: {
    sourceName() {
      return this.sources[this.sourceId]?.name || 'Unknown Source';
    },
    sourceIcon() {
      return this.sources[this.sourceId]?.icon || '';
    }
  },
  methods: {
    goBack() {
      window.location.href = '/data-sources';
    }
  }
}
</script>

<style scoped>
.connector-detail-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
}

.page-header {
  display: flex;
  align-items: center;
  margin-bottom: 32px;
}

.back-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #f3f4f6;
  margin-right: 16px;
  cursor: pointer;
}

.content-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
}

.section-title {
  font-size: 16px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 12px;
}

.source-name-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
}

.oauth-button {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #3A895F;
  color: white;
  padding: 10px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  margin-top: 16px;
  cursor: pointer;
  border: none;
}

.oauth-icon {
  margin-right: 8px;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.cancel-button {
  padding: 8px 16px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background-color: white;
  color: #4b5563;
  font-size: 14px;
  cursor: pointer;
}

.connect-button {
  padding: 8px 16px;
  background-color: #3A895F;
  border: none;
  border-radius: 6px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.setup-guide {
  background-color: #f9fafb;
  border-radius: 8px;
  padding: 24px;
}

.setup-guide-header {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
}

.source-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f3f4f6;
  border-radius: 50%;
  margin-right: 12px;
}

.step {
  margin-bottom: 20px;
}

.step-number {
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
}

.step-details {
  padding-left: 8px;
}

.step-item {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
}

.step-item svg {
  margin-right: 8px;
  flex-shrink: 0;
}

.id-example {
  background-color: #e5e7eb;
  padding: 6px 12px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 14px;
  color: #4b5563;
  margin-top: 8px;
}

.advanced-config-button {
  display: flex;
  align-items: center;
  color: #4b5563;
  background: none;
  border: none;
  padding: 0;
  font-size: 14px;
  cursor: pointer;
}

.advanced-config-button svg {
  margin-right: 4px;
}

@media (max-width: 768px) {
  .content-grid {
    grid-template-columns: 1fr;
  }
  
  .right-column {
    order: -1;
  }
}
</style>
